<script lang="ts" setup>
import type { ProjectActivityStatusEnumType } from '@forgd/shared/enums'

type ActivityStatus = ProjectActivityStatusEnumType | 'not_available'

const props = withDefaults(defineProps<{
  modelValue: ActivityStatus
  labelPrefix?: boolean
  size?: 'md' | 'lg'
}>(), {
  modelValue: 'not_started',
  size: 'md',
})

defineEmits<{
  'update:modelValue': [ProjectActivityStatusEnumType]
}>()

const uiSelect = computed(() => ({
  padding: {
    sm: props.size === 'md' ? 'py-1' : 'py-2',
  },
}))

const uiMenu = {
  padding: 'p-2',
  option: {
    container: 'gap-2',
    padding: 'p-2',
  },
}

const activityItems = {
  completed: { label: 'Done', value: 'completed', color: 'bg-forgd-green-600', bg: 'bg-forgd-green-600/10' },
  in_progress: { label: 'Working on it', value: 'in_progress', color: 'bg-forgd-primary-300', bg: 'bg-forgd-primary-300/10' },
  not_started: { label: 'Not started', value: 'not_started', color: 'bg-forgd-neutral-700', bg: '' },
  on_hold: { label: 'On hold', value: 'on_hold', color: 'bg-forgd-yellow-500', bg: 'bg-forgd-yellow-700/10' },
  stuck: { label: 'Stuck', value: 'stuck', color: 'bg-forgd-red-600', bg: 'bg-forgd-red-600/10' },
}
</script>

<template>
  <UiTooltip v-if="modelValue === 'not_available'" text="This activity depends on completing other activities first to update its status.">
    <div class="bg-forgd-neutral-600/30 flex items-center gap-1 border border-forgd-neutral-700 rounded-xl px-2 py-1">
      <UIcon name="i-heroicons-no-symbol" class="text-forgd-neutral-700 w-4 h-4" />
      <span class="truncate text-forgd-neutral-700  text-xs">Has dependencies</span>
    </div>
  </UiTooltip>
  <USelectMenu
    v-else
    :model-value="modelValue"
    variant="none"
    size="sm"
    class="cursor-pointer min-w-[150px] border border-forgd-neutral-700 rounded-lg"
    :ui="uiSelect"
    :ui-menu="uiMenu"
    :class="[activityItems[modelValue]?.bg]"
    :options="Object.values(activityItems)"
    @update:model-value="e => $emit('update:modelValue', e.value)"
  >
    <template #label>
      <div class="cursor-pointer w-full flex items-center gap-2">
        <span
          class="inline-block h-4 w-4 flex-shrink-0 rounded-full"
          :class="[activityItems[modelValue]?.color]"
          aria-hidden="true"
        />
        <span class="truncate text-forgd-primary-900 text-xs" :class="[size === 'lg' ? 'font-semibold' : '']">
          {{ labelPrefix ? 'Status: ' : '' }} {{ activityItems[modelValue]?.label }}
        </span>
      </div>
    </template>
    <template #option="{ option }">
      <span class="inline-block h-4 w-4 flex-shrink-0 rounded-full" :class="[option.color]" aria-hidden="true" />
      <span class="truncate text-xs">{{ option.label }}</span>
    </template>
  </USelectMenu>
</template>
